/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { MenuProps } from 'antd';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { FlatSubtenant, User } from '@/client';
import { MoreSubtenantsPlaceholder } from '@/components/SubtenantSwitcher/MoreSubtenantsPlaceholder';
import { SubtenantSwitcherOption } from '@/components/SubtenantSwitcher/SubtenantSwitcherOption';
import { TenantOptionForSubtenant } from '@/components/SubtenantSwitcher/TenantOptionForSubtenant';
import { usePaginatedSubtenantsQuery } from '@/hooks/query/usePaginatedSubtenantsQuery';
import { useSubtenantByIdQuery } from '@/hooks/query/useSubtenantByIdQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { useShowErrorMessage } from '@/hooks/useShowErrorMessage';
import { useTheme } from '@/hooks/useTheme';
import { Dropdown } from '@/primitives/Dropdown';
import { antdTokens, tokens } from '@/theme';

import { SubtenantOption } from './SubtenantOption';

export type SubtenantSwitcherProps = {
  currentUser: User | undefined;
  selectedSubtenantID?: string;
  selectedTenantID?: string;
  onSelectSubtenant: (subtenant?: FlatSubtenant) => void;
};

export function SubtenantSwitcher({
  currentUser,
  selectedSubtenantID,
  selectedTenantID,
  onSelectSubtenant,
}: SubtenantSwitcherProps) {
  const { t } = useMimicTranslation('subtenants');
  const { data, isError, updateQueryParams } = usePaginatedSubtenantsQuery({
    params: { flatten: true },
    type: 'inMemory',
  });
  const { data: selectedSubtenantData, isError: isSelectedSubtenantError } = useSubtenantByIdQuery({
    subtenantId: selectedSubtenantID!,
  });

  useShowErrorMessage({ showError: isSelectedSubtenantError, errorMessage: t('errorFetchingSubtenant') });
  useShowErrorMessage({ showError: isError, errorMessage: t('errorFetchingSubtenants') });

  const [tenantHasSubtenants, setTenantHasSubtenants] = useState<boolean | undefined>();
  const [open, setOpen] = useState(false);
  const [inputSearch, setInputSearch] = useState<string>('');
  const [showInput, setShowInput] = useState<boolean>(false);
  const theme = useTheme();

  // Reset state when tenant changes to avoid flash of stale content
  useEffect(() => {
    setTenantHasSubtenants(undefined);
  }, [selectedTenantID]);

  useEffect(() => {
    if (!data) return;
    if (!isNil(data.meta.filters) && !isEmpty(data.meta.filters)) return;
    setTenantHasSubtenants(data.meta.page.totalItems > 0);
  }, [data]);

  const updateSearchTerm = useCallback((term: string) => {
    const trimmedTerm = term.replace(/^\s+|\s+$/g, '');

    if (trimmedTerm.length) {
      updateQueryParams({ filters: { name: trimmedTerm } });
    }
    if (!trimmedTerm.length) {
      updateQueryParams({ filters: { name: undefined } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectSubtenant = (subtenant?: FlatSubtenant) => {
    onSelectSubtenant(subtenant);
    setShowInput(false);
    setInputSearch('');
    setOpen(false);
  };

  const handlePressEnter = (term: string) => {
    if (term.toLowerCase() === data?.data[0]?.name.toLowerCase()) {
      handleSelectSubtenant(data?.data[0]);
      setShowInput(false);
      setOpen(false);
    }
  };

  const subtenants = data?.data;
  const selectedSubtenant = selectedSubtenantData?.data;

  if (!tenantHasSubtenants) return null;

  const hasMoreSubtenants = !!data && data?.meta.page.totalItems > 10;
  const items = dropdownItems(
    currentUser,
    handleSelectSubtenant,
    selectedTenantID!,
    hasMoreSubtenants,
    subtenants,
    selectedSubtenant,
  );
  return (
    <Dropdown
      menu={{
        items: items || [],
        selectable: true,
        style: {
          maxWidth: tokens.color.components.menu.sidebarMenuWidth,
          background: theme.tokens.color.components.menu.menuLvl2Bg,
        },
        defaultSelectedKeys: selectedSubtenant ? [selectedSubtenant.id] : [],
      }}
      onOpenChange={(newState) => setOpen(newState)}
      open={open}
      dataTestId="subtenant-switcher"
    >
      <SubtenantSwitcherOption
        updateSearchTerm={updateSearchTerm}
        selectedSubtenant={selectedSubtenant}
        showInput={showInput}
        setShowInput={setShowInput}
        handlePressEnter={handlePressEnter}
        inputSearch={inputSearch}
        setInputSearch={setInputSearch}
      />
    </Dropdown>
  );
}

function dropdownItems(
  currentUser: User | undefined,
  onSelectSubtenant: SubtenantSwitcherProps['onSelectSubtenant'],
  selectedTenantID: string,
  hasMoreSubtenants: boolean,
  subtenants?: FlatSubtenant[],
  selectedSubtenant?: FlatSubtenant,
): MenuProps['items'] {
  if (!subtenants || subtenants.length === 0) return undefined;

  const items: MenuProps['items'] = [];

  // Tenant shall be shown only to non subtenant-only users
  if (!currentUser?.subtenantOnly) {
    const tenantItem: NonNullable<MenuProps['items']>[number] = {
      label: <TenantOptionForSubtenant selectedSubtenant={selectedSubtenant} />,
      key: selectedTenantID,
      onClick: () => {
        onSelectSubtenant();
      },
      rootClassName: 'tenant-option-for-subtenant',
      style: {
        backgroundColor: !selectedSubtenant ? antdTokens.controlItemBgActive : undefined,
        borderBottom: `1px solid ${tokens.color.border.borderDeeper}`,
      },
    };
    items.push(tenantItem);
  }

  // Add subtenant items
  const subtenantItems: MenuProps['items'] = subtenants.map((subtenant) => ({
    label: <SubtenantOption selectedSubtenant={selectedSubtenant} subtenant={subtenant} />,
    key: subtenant.id,
    onClick: () => {
      onSelectSubtenant(subtenant);
    },
  }));
  items.push(...subtenantItems);

  // Add "more subtenants" placeholder if needed
  if (hasMoreSubtenants) {
    const moreSubtenantsItem: NonNullable<MenuProps['items']>[number] = {
      label: <MoreSubtenantsPlaceholder />,
      key: 'more-subtenants',
      disabled: true,
    };
    items.push(moreSubtenantsItem);
  }

  return items;
}
